import { create } from 'zustand';

const SIDEBAR_COLLAPSED_KEY = 'pert.sidebarCollapsed';

/** Per-viewer preference only — wrapped in try/catch since a private window
 *  or blocked site data can make localStorage throw on access. */
function readStoredSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function writeStoredSidebarCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0');
  } catch {
    // Best-effort — losing the preference on reload isn't worth surfacing an error for.
  }
}

/**
 * UI chrome state only. Anything that decides *what data* a screen shows
 * (filters, sort, page, tab, selected activity) lives in the URL query
 * string instead — see hooks/useUrlParams.ts.
 */
interface UiState {
  mobileSidebarOpen: boolean;
  setMobileSidebarOpen: (open: boolean) => void;

  /** Desktop-only: collapses the persistent sidebar to an icon-only strip. Remembered across reloads. */
  sidebarCollapsed: boolean;
  toggleSidebarCollapsed: () => void;
}

export const useUiStore = create<UiState>((set) => ({
  mobileSidebarOpen: false,
  setMobileSidebarOpen: (open) => set({ mobileSidebarOpen: open }),

  sidebarCollapsed: readStoredSidebarCollapsed(),
  toggleSidebarCollapsed: () =>
    set((s) => {
      const next = !s.sidebarCollapsed;
      writeStoredSidebarCollapsed(next);
      return { sidebarCollapsed: next };
    }),
}));
